import type { Carer, Home } from '@lmc/ts-common'

// The site (home) ids a carer is attached to, mirroring the legacy resolution
// (TeamMemberEditScreen): prefer the multi-site `homes` array, otherwise fall
// back to the singular primary `home`. The legacy add-member flow only ever
// persists the singular `home`, so most users have an empty `homes` and rely on
// that fallback. Genuine group-level users (only a `homeGroup`) resolve to no
// sites. Ids are stringified so callers can compare against string-keyed lookups
// uniformly.
export const carerHomeIds = (carer: Carer): string[] => {
  const ids = carer?.homes?.length
    ? carer.homes
    : carer?.home
      ? [carer.home]
      : []
  return ids.map(String)
}

// Resolves the site (home) names a carer has access to. Caller sources the
// `homes` reference list (typically Redux: `state.data.homes`). Home IDs not
// present in that list are dropped silently (stale references or homes the
// caller can't see). Names are returned in the carer's order, deduped.
export const getSiteNames = (carer: Carer, homes: Home[]): string[] => {
  if (!homes?.length) {
    return []
  }
  const ids = carerHomeIds(carer)
  if (!ids.length) {
    return []
  }
  const byId = new Map(homes.map((h) => [String(h.id), h.name]))
  const seen = new Set<string>()
  const names: string[] = []
  for (const homeId of ids) {
    const name = byId.get(homeId)
    if (name && !seen.has(name)) {
      seen.add(name)
      names.push(name)
    }
  }
  return names
}
